import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import PageShell from "../components/PageShell";
import ImagePlaceholder from "../components/ImagePlaceholder";
import { projects, aboutPortraitImage, categories, latestProject } from "../data/projects";

const heroImage = latestProject.hero;
const projectCount = projects.length;
const disciplineCount = categories.length;

const Home = () => {
  return (
    <PageShell testId="page-home">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-8 md:pt-16 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: "easeOut" }}
            >
              <div className="font-manrope text-[11px] tracking-[0.4em] uppercase text-[#A8D2FA] mb-6">
                Netherlands · Est. 2026
              </div>
              <h1
                data-testid="home-hero-headline"
                className="font-fraunces text-[3rem] leading-[1.02] md:text-[5rem] lg:text-[5.75rem] font-light tracking-tight text-slate-900 dark:text-slate-100 text-balance"
              >
                Photography <br />
                by <em className="italic font-normal text-slate-700 dark:text-slate-200">STXP</em>
              </h1>
              <p
                data-testid="home-hero-subtitle"
                className="mt-8 font-manrope text-base md:text-lg text-slate-600 dark:text-slate-300 leading-relaxed max-w-xl"
              >
                Visual storytelling through portrait, documentary, architecture,
                product and conceptual photography.
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to="/portfolio"
                  data-testid="home-view-portfolio-btn"
                  className="group inline-flex items-center gap-2 rounded-full bg-slate-900 text-white dark:bg-white dark:text-slate-900 px-8 py-4 font-manrope text-[15px] font-medium tracking-wide hover:bg-slate-800 dark:hover:bg-slate-100 hover:-translate-y-0.5 transition-all duration-300 shadow-[0_8px_24px_rgba(15,23,42,0.15)] dark:shadow-[0_10px_32px_rgba(255,255,255,0.06)]"
                >
                  View Portfolio
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/contact"
                  data-testid="home-contact-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-[#E8F4FF] dark:bg-white/[0.08] text-slate-800 dark:text-slate-100 border border-[#C6E2FF]/60 dark:border-white/15 px-8 py-4 font-manrope text-[15px] font-medium tracking-wide hover:bg-[#C6E2FF] dark:hover:bg-white/[0.14] transition-all duration-300"
                >
                  Get in Touch
                </Link>
              </div>

              <div className="mt-16 flex items-center gap-6 font-manrope text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500">
                <div>
                  <div className="font-fraunces text-2xl text-slate-900 dark:text-slate-100">
                    {String(projectCount).padStart(2, "0")}
                  </div>
                  <div className="tracking-widest uppercase">Projects</div>
                </div>
                <div className="w-px h-8 bg-[#C6E2FF]" />
                <div>
                  <div className="font-fraunces text-2xl text-slate-900 dark:text-slate-100">
                    {String(disciplineCount).padStart(2, "0")}
                  </div>
                  <div className="tracking-widest uppercase">Disciplines</div>
                </div>
                <div className="w-px h-8 bg-[#C6E2FF]" />
                <div>
                  <div className="font-fraunces text-2xl text-slate-900 dark:text-slate-100">NL</div>
                  <div className="tracking-widest uppercase">Based</div>
                </div>
              </div>
            </motion.div>
          </div>

          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="relative">
              <Link
                to={`/portfolio/${latestProject.slug}`}
                data-testid="home-hero-image-link"
                className="block group/hero"
              >
                <ImagePlaceholder
                  src={heroImage}
                  alt={`Photograph by STXP — from the series ${latestProject.title}`}
                  ratio="aspect-[4/5]"
                  rounded="rounded-[36px]"
                  label="STXP · HERO FRAME"
                  imgClassName={latestProject.heroObjectPosition || ""}
                  testId="home-hero-image"
                />
              </Link>
              <div className="absolute -bottom-6 -left-6 hidden md:block">
                <Link
                  to={`/portfolio/${latestProject.slug}`}
                  data-testid="home-latest-series-link"
                  className="block group/latest"
                >
                  <div className="glass-strong rounded-2xl px-5 py-4 max-w-[220px] transition-all duration-300 group-hover/latest:-translate-y-0.5 group-hover/latest:shadow-[0_16px_40px_rgba(198,226,255,0.4)]">
                    <div className="font-manrope text-[10px] tracking-[0.3em] uppercase text-[#A8D2FA]">
                      Latest series
                    </div>
                    <div className="font-fraunces text-lg text-slate-900 dark:text-slate-100 mt-1 leading-tight group-hover/latest:text-slate-700 dark:text-slate-200 transition">
                      {latestProject.title}
                    </div>
                  </div>
                </Link>
              </div>
              <div className="absolute -top-4 -right-4">
                <Link
                  to="/contact"
                  data-testid="home-available-badge"
                  className="glass rounded-full px-3 md:px-4 py-1.5 md:py-2 font-manrope text-[9px] md:text-[10px] tracking-[0.25em] md:tracking-[0.3em] uppercase text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition inline-flex items-center gap-1.5"
                >
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                  </span>
                  Available for briefs
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED PROJECTS — every project is a highlight */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24" data-testid="home-featured">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="font-manrope text-[10px] tracking-[0.3em] uppercase text-[#A8D2FA] mb-3">
              Selected work — 2026
            </div>
            <h2 className="font-fraunces text-3xl md:text-5xl font-light text-slate-900 dark:text-slate-100">
              Featured projects
            </h2>
          </div>
          <Link
            to="/portfolio"
            data-testid="home-all-projects-link"
            className="hidden md:inline-flex items-center gap-1.5 font-manrope text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:text-slate-100 transition"
          >
            Browse folders <ArrowUpRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {projects.map((p) => (
            <FeatureCard key={p.slug} project={p} />
          ))}
        </div>
      </section>

      {/* ABOUT PREVIEW */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <ImagePlaceholder
              src={aboutPortraitImage}
              alt="Portrait of STXP"
              ratio="aspect-[4/5]"
              label="STXP · PORTRAIT"
            />
          </div>
          <div className="lg:col-span-7">
            <div className="font-manrope text-[10px] tracking-[0.3em] uppercase text-[#A8D2FA] mb-4">
              About
            </div>
            <p className="font-fraunces text-2xl md:text-4xl font-light text-slate-900 dark:text-slate-100 leading-[1.25] text-balance">
              I&apos;m a photographer based in the Netherlands, interested in visual
              storytelling and creating meaningful imagery through portrait,
              documentary, architectural, product and conceptual photography.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/about"
                data-testid="home-about-link"
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-6 py-3 font-manrope text-sm text-slate-800 dark:text-slate-200 hover:bg-white/70 transition"
              >
                More about me <ArrowRight size={14} />
              </Link>
              <Link
                to="/journal"
                data-testid="home-journal-link"
                className="inline-flex items-center gap-2 rounded-full bg-[#E8F4FF] dark:bg-white/[0.15] border border-[#C6E2FF]/50 dark:border-white/25 px-6 py-3 font-manrope text-sm text-slate-800 dark:text-white hover:bg-[#C6E2FF] dark:hover:bg-white/[0.2] transition"
              >
                Photography Journal
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT TEASER */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16">
        <div className="glass-strong rounded-[36px] p-10 md:p-16 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <div className="font-manrope text-[10px] tracking-[0.3em] uppercase text-[#A8D2FA] mb-4">
              Let&apos;s work together
            </div>
            <h3 className="font-fraunces text-3xl md:text-5xl font-light text-slate-900 dark:text-slate-100 leading-tight">
              Have a story you&apos;d like to tell?
            </h3>
            <p className="mt-5 font-manrope text-slate-600 dark:text-slate-300 max-w-xl">
              Portrait sessions, editorial commissions, architectural studies,
              product photography, or a collaboration you&apos;ve been dreaming about — I&apos;d love to hear from you.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link
              to="/contact"
              data-testid="home-cta-contact"
              className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white dark:bg-white dark:text-slate-900 px-7 py-3.5 font-manrope text-sm font-medium hover:bg-slate-800 dark:hover:bg-slate-100 hover:-translate-y-0.5 transition-all duration-300"
            >
              Start a conversation <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

const FeatureCard = ({ project }) => (
  <Link
    to={`/portfolio/${project.slug}`}
    data-testid={`featured-${project.slug}`}
    className="group relative block"
  >
    <ImagePlaceholder
      src={project.hero}
      alt={`${project.title} — ${project.category}`}
      ratio="aspect-[4/5]"
      label={project.category.toUpperCase()}
      imgClassName={project.heroObjectPosition || ""}
    />
    <div className="mt-4 flex items-start justify-between gap-4">
      <div>
        <div className="font-manrope text-[10px] tracking-[0.3em] uppercase text-slate-400 dark:text-slate-500">
          {project.category} · {project.date}
        </div>
        <h3 className="font-fraunces text-xl md:text-2xl font-light text-slate-900 dark:text-slate-100 mt-1 group-hover:text-slate-700 dark:text-slate-200 transition">
          {project.title}
        </h3>
      </div>
      <ArrowUpRight
        size={18}
        className="text-slate-400 dark:text-slate-500 mt-1 transition-transform group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-slate-900 dark:text-slate-100"
      />
    </div>
  </Link>
);

export default Home;
